import { useEffect } from "react";
import { MandatorType } from "@rentablo/theming";

/**
 * Per-mandator brand fonts, loaded on demand.
 *
 * Each entry is a dynamic `import()` of the mandator's font CSS, so Vite emits
 * it as a separate chunk that is only fetched when that mandator is active.
 * This keeps every deployment shipping just the global Inter font (from
 * `@rentablo/theming/fonts/inter.css`, imported in `index.tsx`) plus the one
 * brand font of the resolved mandator.
 *
 * Mandators not listed here (rentablo, depotcheck_ai) render with the global
 * Inter font and need no entry.
 *
 * Keys are mandator slugs; the folder/file name may differ from the slug
 * (e.g. `quergradaus` uses the `divvyDiary` font, `isarvest` uses `extraetf`).
 * A family shared by several mandators lives in its own folder rather than
 * being duplicated: `kpmg-germany` and `boersennews_de` both load the shared
 * `openSans` font (kpmg additionally loads its own OpenSansCondensed), and
 * `finanzen_net` reuses `finanzenNetZero`'s Work Sans.
 */
const fontLoaders: Partial<Record<MandatorType, () => Promise<unknown>>> = {
  isarvest: () =>
    import("@rentablo/theming/mandators/fonts/extraetf/extraetf.css"),
  vickii: () => import("@rentablo/theming/mandators/fonts/vickii/vickii.css"),
  ftd: () => import("@rentablo/theming/mandators/fonts/ftd/ftd.css"),
  onvista: () =>
    import("@rentablo/theming/mandators/fonts/onvista/onvista.css"),
  "onvista-cashback": () =>
    import("@rentablo/theming/mandators/fonts/onvista/onvista.css"),
  finanzfluss: () =>
    import("@rentablo/theming/mandators/fonts/finanzfluss/finanzfluss.css"),
  finanzen_net_zero: () =>
    import(
      "@rentablo/theming/mandators/fonts/finanzenNetZero/finanzenNetZero.css"
    ),
  finanzen_net: () =>
    import(
      "@rentablo/theming/mandators/fonts/finanzenNetZero/finanzenNetZero.css"
    ),
  "kpmg-germany": () =>
    Promise.all([
      import("@rentablo/theming/mandators/fonts/openSans/openSans.css"),
      import("@rentablo/theming/mandators/fonts/kpmg/kpmg.css"),
    ]),
  boersennews_de: () =>
    import("@rentablo/theming/mandators/fonts/openSans/openSans.css"),
  quergradaus: () =>
    import("@rentablo/theming/mandators/fonts/divvyDiary/divvyDiary.css"),
  ariva_de: () => import("@rentablo/theming/mandators/fonts/ariva/ariva.css"),
  wso: () => import("@rentablo/theming/mandators/fonts/wso/wso.css"),
};

export const useMandatorFonts = (mandatorSlug: MandatorType) => {
  useEffect(() => {
    fontLoaders[mandatorSlug]?.();
  }, [mandatorSlug]);
};
